import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box, Typography, PanelCard, Chip, Icon } from '@glplus/glpweb-dsl';
import { useSetup } from '../lib/SetupContext';
import { useVendor } from '../lib/CapabilitiesContext';
import { POS_SYSTEMS } from '../lib/applyOptions';

/**
 * §2 "Onboarding in progress" (Figma 379:13 / 56:120) — the ASSISTED-track
 * status screen shown while GL Plus sets a vendor's catalog up remotely
 * (Vendor.status === 'onboarding'). Reframed from the old white-glove pipeline:
 * remote (no store visit), unified milestones Profile → Imported → Confirmed →
 * Live, "GL Plus is handling this", and a Switch-to-self-setup escape.
 */

const GREEN = '#25862D';
const ORANGE = '#F67A1A';
const GREY = '#9aa5b1';

type StepState = 'complete' | 'inProgress' | 'pending';

export default function SetupPending() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeMembership } = useVendor();
  const { profile, products } = useSetup();

  const imported = (products?.total ?? 0) > 0;
  const confirmed = (products?.live ?? 0) > 0;
  const posLabel = profile?.posSystem
    ? t(POS_SYSTEMS.find((o) => o.slug === profile.posSystem)?.labelKey ?? '') || profile.posSystem
    : t('setupPending.posFallback');

  const steps: { key: string; state: StepState }[] = [
    { key: 'profile', state: 'complete' },
    { key: 'imported', state: imported ? 'complete' : 'inProgress' },
    { key: 'confirmed', state: confirmed ? 'complete' : imported ? 'inProgress' : 'pending' },
    { key: 'live', state: 'pending' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 880 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {t('setupPending.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {t('setupPending.subtitle')}
          </Typography>
        </Box>
        <Chip label={t('setupPending.chip')} size="small" backgroundColor="#FFF1E3" textColor="#C2410C" fontSize={12} />
      </Box>

      <PanelCard
        accent={GREEN}
        title={t('setupPending.statusTitle')}
        subtitle={t('setupPending.statusSubtitle', { pos: posLabel })}
        sx={{ height: 'auto', bgcolor: '#F3F8F3' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {steps.map((s, i) => (
            <Box
              key={s.key}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderTop: i > 0 ? '1px solid #e2ebe4' : 'none' }}
            >
              <StepIcon state={s.state} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>{t(`setupPending.steps.${s.key}`)}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t(`setupPending.steps.${s.key}Meta`)}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: s.state === 'complete' ? GREEN : s.state === 'inProgress' ? ORANGE : GREY, whiteSpace: 'nowrap' }}>
                {t(`setupPending.state.${s.state}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </PanelCard>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', bgcolor: '#EAF4FF', border: '1px solid #CFE4FB', borderRadius: 2, px: 2, py: 1.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563EB', mt: 0.6, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: '#1f2733' }}>{t('setupPending.handling')}</Typography>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('setupPending.specialistNote', { name: activeMembership?.businessName ?? '' })}{' '}
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/setup')}
          sx={{ border: 'none', background: 'transparent', color: ORANGE, fontWeight: 700, fontSize: 'inherit', cursor: 'pointer', fontFamily: 'inherit', p: 0 }}
        >
          {t('setupPending.switchSelf')}
        </Box>
      </Typography>
    </Box>
  );
}

function StepIcon({ state }: { state: StepState }) {
  if (state === 'complete') {
    return (
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="check" size={15} color="white" ariaLabel="" />
      </Box>
    );
  }
  if (state === 'inProgress') {
    return (
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />
      </Box>
    );
  }
  return <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${GREY}`, flexShrink: 0 }} />;
}
